import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, TableShell, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button,
} from "@/components/ui-kit";
import { Plus, Users, Pencil, Trash2, Eye } from "lucide-react";

const EMPTY = {
  name: "", phone: "", email: "", address: "", city: "", state: "", zip: "",
  preferred_contact: "Phone", cleaning_frequency: "One-time",
  property_notes: "", gate_code: "", parking_instructions: "", customer_notes: "",
};

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { t } = useI18n();

  const load = () => api.get("/clients").then(({ data }) => setClients(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditing(c.id); setOpen(true); };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name) return toast.error(t("Client name is required"));
    try {
      if (editing) await api.put(`/clients/${editing}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? t("Customer updated") : t("Customer added"));
      setOpen(false);
      load();
    } catch (e) { toast.error("Could not save client"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    await api.delete(`/clients/${id}`);
    toast.success(t("Customer deleted"));
    load();
  };

  const filtered = clients.filter((c) =>
    [c.name, c.email, c.phone, c.city].join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Customers"
        subtitle="Your customer directory with full property details."
        search searchValue={q} onSearch={(e) => setQ(e.target.value)}
        action={<PrimaryButton onClick={openNew} data-testid="add-client-button"><Plus size={16} /> {t("Add Customer")}</PrimaryButton>}
      />

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="No customers yet" subtitle="Add your first customer or approve an estimate to create one automatically."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("Add Customer")}</PrimaryButton>} />
      ) : (
        <TableShell headers={["Name", "Contact", "Location", "Frequency", "Actions"]} testid="clients-table">
          {filtered.map((c) => (
            <tr key={c.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4">
                <div className="font-medium text-[#121212]">{c.name}</div>
                <div className="text-xs text-[#52525B]">{c.preferred_contact}</div>
              </td>
              <td className="py-3.5 px-4 text-sm">
                <div>{c.phone}</div>
                <div className="text-xs text-[#52525B]">{c.email}</div>
              </td>
              <td className="py-3.5 px-4 text-sm text-[#52525B]">{[c.city, c.state].filter(Boolean).join(", ")}</td>
              <td className="py-3.5 px-4 text-sm">{c.cleaning_frequency}</td>
              <td className="py-3.5 px-4">
                <div className="flex items-center gap-1">
                  <IconBtn onClick={() => navigate(`/clients/${c.id}`)} title="View" testid={`view-client-${c.id}`}><Eye size={16} /></IconBtn>
                  <IconBtn onClick={() => openEdit(c)} title="Edit"><Pencil size={16} /></IconBtn>
                  <IconBtn onClick={() => remove(c.id)} title="Delete" danger><Trash2 size={16} /></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Customer" : "Add Customer"} size="xl"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-client-button">{t("Save Customer")}</PrimaryButton></>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="client-name-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Preferred Contact"><SelectBox value={form.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} options={["Phone", "Email", "Text"]} /></Field>
          <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
            <Field label="ZIP"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
          </div>
          <Field label="Cleaning Frequency"><SelectBox value={form.cleaning_frequency} onChange={(e) => set("cleaning_frequency", e.target.value)} options={["One-time", "Weekly", "Bi-Weekly", "Monthly", "Quarterly"]} /></Field>
          <Field label="Gate Code"><Input value={form.gate_code} onChange={(e) => set("gate_code", e.target.value)} /></Field>
          <Field label="Parking Instructions" className="sm:col-span-2"><Input value={form.parking_instructions} onChange={(e) => set("parking_instructions", e.target.value)} /></Field>
          <Field label="Property Notes" className="sm:col-span-2"><Textarea rows={2} value={form.property_notes} onChange={(e) => set("property_notes", e.target.value)} /></Field>
          <Field label="Customer Notes" className="sm:col-span-2"><Textarea rows={2} value={form.customer_notes} onChange={(e) => set("customer_notes", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}

export function IconBtn({ children, onClick, title, danger, testid }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className={`p-2 rounded-lg transition ${danger ? "text-red-500 hover:bg-red-50" : "text-[#5A189A] hover:bg-[#f4eefb]"}`}>
      {children}
    </button>
  );
}
